import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { preRegistrationsApi } from "../../preRegistrations/api/preRegistrations";
import { SectionCard } from "./SectionCard";

const QUERY_KEY = ["pre-registration-notification-recipients", "global"];

export const NotificationRecipientsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: "", email: "" });
  const [addError, setAddError] = useState<string | null>(null);

  const recipientsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => preRegistrationsApi.listNotificationRecipients({ scope: "global" }),
    staleTime: 2 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      preRegistrationsApi.createNotificationRecipient({
        name: form.name.trim() || undefined,
        email: form.email.trim(),
        client_id: null, // global — no client scope
      }),
    onSuccess: () => {
      setForm({ name: "", email: "" });
      setAddError(null);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? "Failed to add recipient";
      setAddError(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (recipientId: string) =>
      preRegistrationsApi.deleteNotificationRecipient(recipientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const handleAdd = () => {
    setAddError(null);
    createMutation.mutate();
  };

  return (
    <Box>
      <Stack spacing={3}>
        <SectionCard
          title="Global Notification Recipients"
          description="These addresses receive a notification email whenever any client's pre-registration is submitted — regardless of which client the applicant belongs to. Manage client-specific recipients on the Pre-Registrations page."
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                fullWidth
                size="small"
              />
              <Button
                variant="contained"
                disabled={createMutation.isPending || !form.email.trim()}
                onClick={handleAdd}
                sx={{ whiteSpace: "nowrap", minWidth: 140 }}
              >
                {createMutation.isPending ? "Adding…" : "Add recipient"}
              </Button>
            </Stack>

            {addError && <Alert severity="error">{addError}</Alert>}

            {recipientsQuery.isLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Loading…</Typography>
              </Stack>
            ) : recipientsQuery.isError ? (
              <Alert severity="error">Failed to load recipients.</Alert>
            ) : recipientsQuery.data?.data?.length ? (
              <Stack spacing={1}>
                {recipientsQuery.data.data.map((r) => (
                  <Stack
                    key={r.id}
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ md: "center" }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight={600}>
                        {r.name || r.email}
                      </Typography>
                      {r.name && (
                        <Typography variant="body2" color="text.secondary">
                          {r.email}
                        </Typography>
                      )}
                      <Chip label="Global" size="small" variant="outlined" color="primary" />
                    </Stack>
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      disabled={deleteMutation.isPending}
                      onClick={() => deleteMutation.mutate(r.id)}
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">
                No global notification recipients configured yet.
              </Alert>
            )}
          </Stack>
        </SectionCard>
      </Stack>
    </Box>
  );
};
